import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const Sidebar = ({ user, todayLeadsCount = 0, unreadMessages = 0, onClose, isOpen }) => {
    const location = useLocation();
    const { isDark } = useTheme();

    // Navigation configuration
    const navSections = [
        {
            title: 'Main',
            items: [
                { name: 'Dashboard', href: '/dashboard', icon: 'dashboard', count: null },
                { name: 'Leads', href: '/leads', icon: 'leaderboard', count: todayLeadsCount, countColorDark: 'bg-pink text-white', countColorLight: 'bg-primary text-darkTeal' },
                { name: 'Messages', href: '/messages', icon: 'chat', disabled: true, badge: 'Coming Soon', badgeColor: 'bg-orange-500/20 text-orange-500' },
                { name: 'Calendar', href: '/calendar', icon: 'calendar_month', disabled: true, badge: 'Coming Soon', badgeColor: 'bg-orange-500/20 text-orange-500' },
            ]
        },
        {
            title: 'Management',
            items: [
                { name: 'Templates', href: '/templates', icon: 'wysiwyg', count: null },
                { name: 'Channels', href: '/channels', icon: 'hub', indicator: true },
                { name: 'Lead Sources', href: '/lead-sources', icon: 'source', count: null },
            ]
        },
        {
            title: 'Account',
            items: [
                { name: 'Settings', href: '/settings', icon: 'settings', count: null },
                { name: 'Billing', href: '/billing', icon: 'credit_card', count: null },
                { name: 'Help', href: '/help', icon: 'help', count: null },
            ]
        }
    ];

    const isActive = (path) => {
        return location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));
    };

    return (
        <>
            {/* Mobile backdrop */}
            <div
                className={`fixed inset-0 z-[60] backdrop-blur-sm transition-opacity lg:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${isDark ? 'bg-navy/80' : 'bg-darkTeal/30'}`}
                onClick={onClose}
            ></div>

            {/* Sidebar */}
            <aside className={`w-64 fixed left-0 bottom-0 top-0 lg:top-16 flex flex-col z-[70] transform transition-transform duration-300 lg:translate-x-0 border-r ${isOpen ? 'translate-x-0' : '-translate-x-full'} ${isDark ? 'bg-navy border-gray-700' : 'bg-mint border-teal/20'}`}>
                {/* Mobile Header for Sidebar (Logo) */}
                <div className={`flex items-center gap-2 px-6 h-16 border-b lg:hidden ${isDark ? 'border-white/10' : 'border-teal/10'}`}>
                    <Link to="/" className="flex items-center gap-2" onClick={onClose}>
                        <span className={`material-symbols-outlined text-3xl ${isDark ? 'text-cyan' : 'text-primary'}`}>bolt</span>
                        <h1 className={`text-xl font-bold tracking-tight ${isDark ? 'text-cyan' : 'text-teal'}`}>OffTrigger</h1>
                    </Link>
                    <button onClick={onClose} className={`ml-auto ${isDark ? 'text-light/50 hover:text-light' : 'text-darkTeal/50 hover:text-darkTeal'}`}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto py-4 px-3">
                    {navSections.map((section, index) => (
                        <div key={index} className="mb-5 last:mb-0">
                            <p className={`text-[11px] font-bold uppercase tracking-wider mb-2 px-3 text-left ${isDark ? 'text-light/40' : 'text-darkTeal/50'}`}>{section.title}</p>
                            <nav className="space-y-1">
                                {section.items.map((item) => {
                                    const active = isActive(item.href);
                                    const disabled = item.disabled;

                                    // Hide items on mobile that are already in the bottom nav
                                    const isDuplicateOnMobile = ['/dashboard', '/leads', '/channels'].includes(item.href);

                                    return (
                                        <Link
                                            key={item.name}
                                            to={disabled ? '#' : item.href}
                                            onClick={disabled ? (e) => e.preventDefault() : onClose}
                                            className={`${isDuplicateOnMobile ? 'hidden lg:flex' : 'flex'} items-center gap-3 px-3 py-2 rounded-lg transition-all group text-[13px] ${disabled ? 'opacity-50 cursor-not-allowed' : ''
                                                } ${active
                                                    ? isDark
                                                        ? 'bg-cyan/10 text-cyan border-l-4 border-cyan font-semibold'
                                                        : 'bg-primary/20 text-teal border-l-4 border-primary font-semibold'
                                                    : isDark
                                                        ? 'text-light/70 hover:bg-light/5 hover:text-light'
                                                        : 'text-darkTeal/70 hover:bg-teal/5 hover:text-darkTeal'
                                                }`}
                                        >
                                            <span className={`material-symbols-outlined text-[20px] transition-colors ${active ? '' : isDark ? 'group-hover:text-cyan' : 'group-hover:text-primary'}`}>
                                                {item.icon}
                                            </span>
                                            <span>{item.name}</span>

                                            {/* Badge count or custom badge */}
                                            {item.badge ? (
                                                <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded ${item.badgeColor || 'bg-orange-500/20 text-orange-500'}`}>
                                                    {item.badge}
                                                </span>
                                            ) : (item.count !== null && item.count > 0 && (
                                                <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isDark ? (item.countColorDark || 'bg-cyan text-navy') : (item.countColorLight || 'bg-primary text-darkTeal')}`}>
                                                    {item.count}
                                                </span>
                                            ))}

                                            {/* Status Indicator (for Channels) */}
                                            {item.indicator && (
                                                <span className={`ml-auto w-2 h-2 rounded-full ${isDark ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-primary shadow-[0_0_8px_rgba(48,227,202,0.6)]'}`}></span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </nav>
                        </div>
                    ))}
                </div>

                {/* User Profile Footer */}
                <div className={`px-3 py-3 border-t ${isDark ? 'border-gray-700 bg-navy' : 'border-teal/20 bg-mint'}`}>
                    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors border ${isDark ? 'bg-navy hover:bg-light/5 border-light/10' : 'bg-mint hover:bg-teal/5 border-teal/10'}`}>
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isDark ? 'bg-light/10 text-cyan' : 'bg-teal/10 text-primary'}`}>
                            {user?.avatar_url ? (
                                <img src={user.avatar_url} alt="User Avatar" className="w-full h-full rounded-full object-cover" />
                            ) : (
                                <span className="font-bold text-sm">
                                    {user?.full_name ? user.full_name.charAt(0).toUpperCase() : <span className="material-symbols-outlined text-[20px]">person</span>}
                                </span>
                            )}
                        </div>
                        <div className="overflow-hidden">
                            <h4 className={`text-[13px] font-bold truncate text-left ${isDark ? 'text-light' : 'text-darkTeal'}`}>{user?.full_name || 'User'}</h4>
                            <p className={`text-[11px] truncate ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>{user?.email}</p>
                        </div>
                    </div>
                    <div className="mt-2 text-left">
                        <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm w-full block text-center ${isDark ? 'text-navy bg-cyan' : 'text-mint bg-teal'}`}>{user?.subscription_plan || 'Free'}</span>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
